import React, { ReactNode } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import { DesktopNavBarMenu, MobileNavBarMenu } from "@app.automotus.io/components/NavBar/NavBarMenu";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Slide from "@mui/material/Slide";
import useScrollTrigger from "@mui/material/useScrollTrigger";
import LogoBlue from "@app.automotus.io/components/images/AppLogo/logoBlue";
import {
  GET_ALL_INVOICES,
  GET_PENDING_ENFORCEMENT_INTENTS_OVERVIEW,
  GET_PERMIT_STATS,
  GetAllInvoicesData,
  GetPendingEnforcementIntentsData,
  GetPermitStatsData,
} from "common/graphql";
import { useQuery } from "@apollo/client";
import { Theme } from "@mui/material/styles";
import { AccountContextBanner } from "@app.automotus.io/components/AccountContextBanner";

/**
 * Component that renders the nav bar at the top of the dashboard.
 */
export const NavBar: React.FC<NavBarProps> = (props) => {
  const isMobile = useIsMobile();
  const toolbarRef = React.useRef(null);
  const scrollTrigger = useScrollTrigger({
    target: props.target?.current || undefined,
  });

  const { data: invoicesData, loading: invoicesLoading } = useQuery<GetAllInvoicesData>(GET_ALL_INVOICES);
  const { data: pendingEnforcementIntentsData, loading: pendingEnforcementIntentsLoading } =
    useQuery<GetPendingEnforcementIntentsData>(GET_PENDING_ENFORCEMENT_INTENTS_OVERVIEW);
  const { data: permitStatsData } = useQuery<GetPermitStatsData>(GET_PERMIT_STATS);
  const pendingPermitsCount =
    (permitStatsData?.pendingPermits?.aggregate?.count ?? 0) +
    (permitStatsData?.awaitingInfoPermits?.aggregate?.count ?? 0);
  return (
    <Slide appear={false} in={!scrollTrigger}>
      <AppBar
        position={"fixed"}
        sx={{
          bgcolor: (theme: Theme) => theme.palette.background.paper,
          color: (theme: Theme) => theme.palette.text.primary,
        }}
      >
        <Toolbar
          sx={{
            height: { xs: 54, lg: 88 },
            // MUI's default Toolbar applies `min-height: 64` at the theme's `sm`
            // breakpoint (450px here), which overrides our `height: 54` and
            // makes the navbar 10px taller than NAVBAR_HEIGHT advertises —
            // partially clipping the AccountContextBanner anchored just below.
            px: { xs: 2, md: 5 },
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
          }}
          ref={toolbarRef}
        >
          <LogoBlue sx={{ height: { xs: 22, lg: 40 }, width: "auto" }} />
          {isMobile ? (
            <MobileNavBarMenu invoicesData={invoicesData} invoicesLoading={invoicesLoading} {...props} />
          ) : (
            <DesktopNavBarMenu
              invoicesData={invoicesData}
              invoicesLoading={invoicesLoading}
              pendingEnforcementIntentsData={pendingEnforcementIntentsData}
              pendingEnforcementIntentsLoading={pendingEnforcementIntentsLoading}
              pendingPermitsCount={pendingPermitsCount}
              {...props}
            />
          )}
        </Toolbar>
        <AccountContextBanner />
      </AppBar>
    </Slide>
  );
};

export const NAVBAR_HEIGHT = { xs: 54, tiny: 54, sm: 54, md: 54, lg: 88, xl: 88 };

export interface NavBarProps {
  targets: NavBarTarget[];
  target?: React.RefObject<Node | Window | undefined>;
  /** Label for the Support entry in the account (avatar) dropdown menu. Defaults to "Support". */
  supportLabel?: string;
  /**
   * URL for the Support entry in the account (avatar) dropdown menu. Each app passes its own —
   * no default. Only required where the dropdown can actually render (i.e. `userProfile` is set).
   */
  supportUrl?: string;
  /** Label for the Resources entry in the account (avatar) dropdown menu. Defaults to "Resources". */
  resourcesLabel?: string;
  /** URL for the Resources entry in the account (avatar) dropdown menu. Omit to not render it. */
  resourcesUrl?: string;
}

export interface NavBarTarget {
  to: string;
  title: string;
  onClick?: (e: React.SyntheticEvent) => void;
  icon?: ReactNode;
  /** When "external", the mobile NavBar opens `to` in a new tab instead of routing to it internally. */
  linkType?: "external" | "profileLink";
  /**
   * When true, suppresses rendering `icon` in the mobile NavBar while still
   * showing it in the desktop NavBar. Use this when a target should show an
   * icon on desktop but not on mobile (e.g. curbsuite's Support tab).
   */
  hideIconOnMobile?: boolean;
}

export default NavBar;
